import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import * as types from "types";
import useThreadsSuspenseQuery from "services/dbApi/hooks/useThreadsSuspenseQuery";
import usePatchThreadMutation from "services/dbApi/hooks/usePatchThreadMutation";

type ThreadAction =
  | types.ChatThread
  | ((prev: types.ChatThread) => types.ChatThread);

export type UseCurrentThreadReturn = readonly [
  thread: types.ChatThread,
  /**
   * Updates the thread in-memory.
   * Chat threads are held in the React Query cache.
   * Use for rapid changes (e.g. each token of a streaming title).
   */
  setThread: (action: ThreadAction) => void,
  /**
   * Updates the thread in-memory AND in the database.
   * Database persistence through `PATCH /threads/:id`.
   * Use to commit a final value (e.g. once a streaming title has finished,
   * when the user picks a persona, when a rename is saved).
   */
  commitThread: (action: ThreadAction) => void
];

/**
 * Reads the current thread from the React Query threads cache and returns
 * useState-style writers for it. The cache is the single source of truth —
 * `setThread` updates it locally, `commitThread` updates it locally AND
 * persists to the backend.
 *
 * Suspends while the threads list is loading. Throws "Thread not found" if
 * the threads list is loaded but contains no row matching `threadId` — the
 * Chat error boundary catches this and renders the 404 fallback.
 */
export function useCurrentThread(threadId: string): UseCurrentThreadReturn {
  const queryClient = useQueryClient();
  const patchThreadMutation = usePatchThreadMutation();

  const threadsQuery = useThreadsSuspenseQuery({
    select: (threads) => threads.find((t) => t.id === threadId),
  });
  const thread = threadsQuery.data;
  if (thread === undefined) throw new Error(`Thread not found: ${threadId}`);

  const setThread = React.useCallback(
    (action: ThreadAction) => {
      queryClient.setQueryData<types.ChatThread[]>(
        ["cc-db-api", "threads"],
        (prev) =>
          prev?.map((t) => {
            if (t.id !== threadId) return t;
            return typeof action === "function" ? action(t) : action;
          })
      );
    },
    [queryClient, threadId]
  );

  const commitThread = React.useCallback(
    (action: ThreadAction) => {
      let newThread: types.ChatThread | undefined;

      // Update thread in-memory.
      queryClient.setQueryData<types.ChatThread[]>(
        ["cc-db-api", "threads"],
        (prev) =>
          prev?.map((t) => {
            if (t.id !== threadId) return t;
            newThread = typeof action === "function" ? action(t) : action;
            return newThread;
          })
      );

      // No row matched — nothing to persist.
      if (!newThread) return;

      // Persist the editable fields. `timestamp` is server-bumped on message
      // writes and intentionally not sent here so we don't clobber it.
      patchThreadMutation.mutate({
        threadId,
        patch: {
          name: newThread.name,
          personaId: newThread.personaId,
        },
      });
    },
    [queryClient, threadId, patchThreadMutation]
  );

  return [thread, setThread, commitThread] as const;
}
